import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useNavigate, Routes } from 'react-router-dom';
import { ProjectType, Agent, RitzAPI, PasswordExpirationPolicy } from '@moderepo/mode-internal-apis';
import { selectLoggedInAgent, selectProjectAgent, selectProjectById, selectProjectUserById, selectSudoProjectAccessKey,
    selectSudoUserAccessKey, setSudoProjectAccessKey, setSudoUserAccessKey, ritzDataContext, selectAgentAuth } from '@moderepo/mode-data-state-ritz';
import { AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS, deleteLocalStorageItem, LoadingScreen, Time } from '@moderepo/mode-app-components';
import { modeUIContext, selectCache } from '@moderepo/mode-ui-state';
import * as Constants from '../../utils/constants';
import { RouteHelper } from './RouteHelper';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { loadRequiredData, RouteRequiredData } from './RouteDataHelper';
import { DEFAULT_ROUTE_PATHS } from './AppRoutes';
import { PASSWORD_EXPIRED_ALERT_SEEN_CACHE_KEY, redirectUserToAnotherOrganization, useGetURLParamEntityIdAsString }
    from '../../utils/utils';
import { getOrganization } from '../app/App';

/**
 * The otherParams will be like this 'users/1785/homes/1653'
 */
const getUserIdFromOtherParams = (otherParams: string | undefined): number | undefined => {
    if (otherParams) {
        // Break the params into tokens separated by '/'
        const tokens = otherParams.split('/');

        // Find the token equal to 'users'
        const userParamIndex = tokens.findIndex((token) => {
            return token === 'users';
        });

        if (userParamIndex >= 0) {
            // If we found a token 'users' and the next token after that is a number, we can assume that token is the user id value
            const nextToken = tokens[userParamIndex + 1];
            if (nextToken !== undefined && !Number.isNaN(Number(nextToken))) {
                return Number(tokens[userParamIndex + 1]);
            }
        }
    }
    return undefined;
};


/**
 * This is the entry point for all routes that are in project context. This component will check to see which type of project the user is
 * on and return the set of Routes for that project type.
 */
export const ProjectRoutesSwitch = () => {

    const params = useParams();
    const navigate = useNavigate();
    const projectId = Number(params.projectId);

    /**
     * IMPORTANT NOTE: The params will be like this. The userId is NOT parsed therefore we need to parse it ourselves.
     *      {
     *          projectId: 1234,
     *          *: 'users/5678/homes/4345'
     *      }
     */
    const otherParams = useGetURLParamEntityIdAsString('*');
    const userId = getUserIdFromOtherParams(otherParams);

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:uiState } = useContext(modeUIContext);
    const auth = selectAgentAuth(dataState);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const projectAgent = loggedInAgent && projectId ? selectProjectAgent(dataState, projectId, loggedInAgent.id) : undefined;
    const projectKey = selectSudoProjectAccessKey(dataState, projectId);
    const userKey = userId ? selectSudoUserAccessKey(dataState, userId) : undefined;
    const user = userId ? selectProjectUserById(dataState, userId) : undefined;
    const routeHelper = RouteHelper.getInstance(navigate);
    const organization = getOrganization();


    // Keep track of the current page pathname. If this pathname changed, we know the user moved to a different page so we can
    // update the requiredData accordingly
    const [requiredData, setRequiredData] = useState<RouteRequiredData | undefined>();


    useEffect(() => {
        const loadData = async () => {
            const data = await loadRequiredData(
                projectId, userId, dataDispatch, auth.agentLoginInfo, auth.loggedInAgent, project, projectAgent, projectKey, userKey, user,
            );
            setRequiredData(data);
        };

        loadData();
    }, [auth.agentLoginInfo, auth.loggedInAgent, dataDispatch, project, projectAgent, projectId, projectKey, user, userId, userKey]);



    /**
     * This useEffect will create a timer to automatically renew the projectKey and userKey
     */
    useEffect(() => {
        const timer = window.setInterval(async () => {

            // If key is expired or will expire before the next check, re-fetch a new key
            if (projectKey && Date.parse(projectKey.expirationTime) <= Date.now() + AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS + Time.MINUTE_IN_MS) {
                try {
                    const key = await RitzAPI.getInstance().generateProjectKey(projectId);
                    dataDispatch(setSudoProjectAccessKey(projectId, key));
                } catch (error) {
                    // do nothing
                }
            }

            // If key is expired or will expire before the next check, re-fetch a new key
            if (userKey && userId && Date.parse(userKey.expirationTime) <= Date.now() + AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS + Time.MINUTE_IN_MS) {
                try {
                    const key = await RitzAPI.getInstance().createUserKey(projectId, userId);
                    dataDispatch(setSudoUserAccessKey(userId, key));
                } catch (error) {
                    // do nothing
                }
            }
        }, Time.MINUTE_IN_MS);


        // Return a clean up function that will stop the timer when component unload
        return () => {
            window.clearInterval(timer);
        };

    }, [dataDispatch, projectId, projectKey, userId, userKey]);



    // Get the DEFAULT list of routes that all projects have and useMemo to cache it
    const defaultRoutes: readonly JSX.Element[] = useMemo(() => {
        const routesHandler = ProjectTypeHandlersManager.getInstance().getRoutesHandler(ProjectType.DEFAULT);
        if (routesHandler) {
            return routesHandler.getRoutesComponent(project, loggedInAgent);
        }
        return [];
    }, [loggedInAgent, project]);


    // Get the list of project specific routes for based on project type and useMemo to cache it
    const projectSpecificRoutes: readonly JSX.Element[] = useMemo(() => {
        const routesHandler = ProjectTypeHandlersManager.getInstance().getRoutesHandler(project?.supportToolSettings.projectType);
        if (routesHandler) {
            return routesHandler.getRoutesComponent(project, loggedInAgent);
        }
        return [];
    }, [loggedInAgent, project]);


    // combine both set of routes and cache them so we don't need to do this many times
    const allRoutes = useMemo(() => {
        const projectRoutesComponents = projectSpecificRoutes.map((route: JSX.Element): JSX.Element => {
            return {
                ...route,
                key: route.props.path,
            };
        });

        const defaultRoutesComponents = defaultRoutes.map((route: JSX.Element): JSX.Element => {
            return {
                ...route,
                key: route.props.path,
            };
        });

        // 'react-router-dom' 6 doesn't support absolute path for sub routes inside another routes. Therefore,
        // we need to convert absolute path to relative path by removing the '/projects/:projectId' from the path. We will assume
        // the user get here because they are on paths that start with '/projects/:projectId/*'
        return [...projectRoutesComponents, ...defaultRoutesComponents].map((route) => {
            const path = route.props.path;
            const relativePath = path.startsWith(`${DEFAULT_ROUTE_PATHS.PROJECTS_$projectId}`)
                ? path.substring(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId.length + 1)
                : path;
            return {
                ...route,
                props: {
                    ...route.props,
                    path: relativePath,
                },
            };
        });
    }, [defaultRoutes, projectSpecificRoutes]);


    // If there is no organization, that mean we weren't able get the organization based on the baseUrl or the orgId in the path.
    if (!organization) {
        return (
            <Navigate to={routeHelper.getNotAuthorizedPageUrl()} />
        );
    }


    if (!requiredData) {
        return (
            <LoadingScreen />
        );
    }



    // LOADING DATA COMPLETED. Now we can check for required data and display the page if everything is good


    // This page requires the agent to be logged in so if there is no loggedInAgent data, we need to redirect the agent to the login page
    if (!loggedInAgent) {
        // We tried but can't get agent's profile. We need to take the agent to the login page.
        // Before redirect the agent, we need to clear all login info from localStorage and RitzAPI
        deleteLocalStorageItem(Constants.LS_KEY_LOGIN_INFO);

        return (
            <Navigate to={routeHelper.getLoginPageUrl()} />
        );
    }


    // Required data loaded but the route contains userId so we need to wait for userKey to be fetched
    if (userId && !userKey) {
        return (
            <LoadingScreen />
        );
    }


    if (!loggedInAgent.verified) {
        // We got the agent data but the agent is not verified so we need to redirect the agent to the My Account page.
        // This is the only page the agent will have access to
        return (
            <Navigate to={routeHelper.getMyAccountPageUrl()} />
        );
    }


    /**
     * If the user tries to enter a page of an organization that doesn't match the agent's current logged in organization, then we will
     * redirect the user to their logged in organization.
     */
    if (loggedInAgent && loggedInAgent.orgId !== organization.id) {
        redirectUserToAnotherOrganization(organization.id, loggedInAgent.orgId);
        // Return loading screen so that we don't continue doing anything else while we redirect
        return (
            <LoadingScreen />
        );
    }


    if (loggedInAgent.passwordUpdateTime !== undefined && organization.authSchemes.passwordPolicy?.maxAge) {
        // If organization's passwordPolicy has "maxAge" settings, we need to check the last time the user changed their password.
        // If they changed their password longer than maxAge, take the user to the my account page to change the password

        // maxAge is in DAYS, we need to convert maxAge to milliseconds
        const maxAgeInMS = organization.authSchemes.passwordPolicy.maxAge * Time.DAY_IN_MS;
        const passwordUpdatedTS = Date.parse(loggedInAgent.passwordUpdateTime);
        const isPasswordExpired = passwordUpdatedTS + maxAgeInMS < Date.now();

        if (isPasswordExpired) {
            if (organization.authSchemes.passwordPolicy.expirationPolicy === PasswordExpirationPolicy.BLOCK) {
                // If the password expiration Policy is BLOCK, don't let the user go anywhere else
                return (
                    <Navigate to={routeHelper.getMyAccountPageUrl()} />
                );
            }

            const expirePasswordAlertSeenState = selectCache(uiState, PASSWORD_EXPIRED_ALERT_SEEN_CACHE_KEY);
            if (!expirePasswordAlertSeenState?.[organization.id]) {
                // If the password expiration Policy is WARN, we will also take the user to the My Account page BUT only
                // if the user has not seen the password expired alert yet for the current organization.
                return (
                    <Navigate to={routeHelper.getMyAccountPageUrl()} />
                );
            }
        }
    }



    if (!project || !projectKey) {
        // The agent is logged in but we are not able to get the project data. This is probably because the project doesn't exist
        // or the agent does not have access to the project.
        return (
            <Navigate to={routeHelper.getNotAuthorizedPageUrl()} />
        );
    }


    // We have everything we needed so now we can display the page
    return (
        <Routes>
            {allRoutes}
        </Routes>
    );
};
